import { type SearchParamsLike, isMetaPaidUtm } from "@/lib/age-group/policy";

/**
 * Channel a visitor arrived from via a paid ad. Only "meta" exists today;
 * "google" can be added when Google Ads launches (see PLA-423).
 */
export type PaidAdChannel = "meta";

const PAID_AD_CHANNEL_KEY = "playmath.paidAdChannel";
export const PAID_AD_CHANNEL_CHANGED_EVENT = "playmath:paidAdChannelChanged";

function isPaidAdChannel(value: string | null): value is PaidAdChannel {
  return value === "meta";
}

/**
 * Detects whether the current landing URL indicates a paid-ad arrival.
 *
 * Meta is detected via `isMetaPaidUtm` (see lib/age-group/policy.ts): any
 * Meta-family `utm_source`. All our Meta traffic is paid, and partners tag the
 * medium inconsistently, so the source alone is the signal.
 *
 * We deliberately do NOT treat a bare `fbclid` as a paid-ad signal: Facebook
 * appends `fbclid` to organic link clicks/shares too (not just ads), so keying
 * on it would load the Meta Pixel on non-campaign visitors — including children
 * arriving from a shared link. Requiring a Meta `utm_source` keeps the pixel
 * scoped to ad campaigns and matches our privacy-policy representation.
 */
export function getPaidAdChannel(searchParams: SearchParamsLike): PaidAdChannel | null {
  if (isMetaPaidUtm(searchParams)) {
    return "meta";
  }
  return null;
}

/**
 * Persists the detected channel to sessionStorage once per session (it does not
 * overwrite an existing value), and notifies listeners so `usePaidAdChannel`
 * can react within the landing render. No-op when channel is null.
 */
export function persistPaidAdChannel(channel: PaidAdChannel | null): void {
  if (!channel) return;
  try {
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(PAID_AD_CHANNEL_KEY)) return;
    window.sessionStorage.setItem(PAID_AD_CHANNEL_KEY, channel);
    window.dispatchEvent(new CustomEvent(PAID_AD_CHANNEL_CHANGED_EVENT, { detail: { channel } }));
  } catch {
    // Non-critical: sessionStorage may be unavailable
  }
}

export function getStoredPaidAdChannel(): PaidAdChannel | null {
  try {
    if (typeof window === "undefined") return null;
    const value = window.sessionStorage.getItem(PAID_AD_CHANNEL_KEY);
    return isPaidAdChannel(value) ? value : null;
  } catch {
    return null;
  }
}
